<script setup lang="ts">
import {apps, Commands, formatting, type Models, type ViewContexts} from "@hantera/portal-app";
import {useClose} from "@hantera/design-system";
import useApps from "@/composition/use-apps";
import {ref} from "vue";

const props = defineProps<{
  orderViewContext: ViewContexts.OrderViewContext
  delivery: Models.Graph.Delivery
}>()

const appsContext = useApps();

const services = appsContext.getServices(apps.standardServices.shippingProductsService);

const products: apps.standardServices.ShippingProduct[] = [];
const close = useClose()

for (const s of services) {

  products.unshift(...await s.getShippingProducts(props.orderViewContext.state, props.delivery))

}

const selected = ref<apps.standardServices.ShippingProduct | undefined>(products.find(r => r.productNumber === props.delivery.shippingProductNumber));

async function update() {

  const commands: Commands.Order.OrderCommand[] = [];

  if (!selected.value) {
    commands.push({
      type: 'setShippingProduct',
      deliveryId: props.delivery.deliveryId,
      productNumber: undefined,
      description: undefined
    })

    commands.push({
      type: 'setShippingPrice',
      deliveryId: props.delivery.deliveryId,
      shippingPrice: 0
    })

    await props.orderViewContext.pushPendingChange(
      'set_shipping_' + props.delivery.deliveryId,
      commands,
      'Removed shipping of {deliveryNumber}',
      {
        deliveryNumber: props.delivery.deliveryNumber,

      }
    )
  } else {

    commands.push({
      type: 'setShippingProduct',
      deliveryId: props.delivery.deliveryId,
      productNumber: selected.value?.productNumber,
      description: selected.value?.description
    })

    commands.push({
      type: 'setShippingPrice',
      deliveryId: props.delivery.deliveryId,
      shippingPrice: selected.value?.price ?? 0
    })

    if (selected.value?.dynamic) {
      commands.push({
        type: 'setDeliveryDynamicFields',
        deliveryId: props.delivery.deliveryId,
        fields: selected.value?.dynamic
      })
    }

    await props.orderViewContext.pushPendingChange(
      'set_shipping_' + props.delivery.deliveryId,
      commands,
      'Set shipping product to {shippingProductNumber} of {deliveryNumber}',
      {
        shippingProductNumber: selected.value?.productNumber,
        deliveryNumber: props.delivery.deliveryNumber
      }
    )
  }

  close()

}

</script>

<template>
  
  <div class="options">
    
    <div class="box-content p neat-scroll option-items">
    
      <label
        v-for="product in products"
        :key="product.optionId ?? product.productNumber"
        class="option"
      >
        <span class="radio-container">
          <input type="radio" :value="product" v-model="selected" />
        </span>
        <span class="product-number">
          {{ product.productNumber }}
        </span>
        <span class="description">
          {{ product.description }}
          <span class="additional-info">
            {{ product.additionalInformation }}
          </span>
        </span>
        
        <span class="price">
          {{ formatting.currency(product.price, orderViewContext.state.currencyCode) }}
        </span>
      </label>

      <label
        class="option option-none"
      >
        <span class="radio-container">
          <input type="radio" :value="undefined" v-model="selected" />
        </span>
        <span class="product-number">
          None
        </span>
      </label>
      
      
    </div>
    
    <div class="actions">
      
      <button class="btn-link" @click="close">Cancel</button>
      <button class="btn-primary" @click="update">OK</button>
      
    </div>
  </div>
  
</template>

<style scoped>
.option {
  display: grid;
  grid-template-columns: min-content auto max-content;
  grid-template-rows: max-content max-content;
  column-gap: 10px;
  border-radius: 5px;
  border: 1px solid hsl(var(--color-100));
  padding: 5px;
}

.option-items {
  max-height: 70vh;
  overflow: auto;
}

.option:has(input:checked) {
  border-color: hsl(var(--color-primary));

}

.radio-container {
  grid-row: 1 / 3;
  display: flex;
  align-items: center;
  justify-content: center;
}

.description {
  grid-row: 2 / 3;
}

.price {
  grid-row: 1 / 3;
  display: flex;
  align-items: center;
  justify-content: center;
}

.product-number {
  font-weight: bold;
}

.option-none .product-number {
  font-style: italic;
}

.additional-info {
  display: block;
  opacity: .6;
  margin-top: 5px;
}

.actions {
  margin-top: 10px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
</style>